import { UseFormReturn } from 'react-hook-form';
import { IDocumentTable } from '@models/Forms/IForms';
import './Table.scss';
import { IFieldElem } from '@models/IFormData';
import { useDeferredValue, useEffect } from 'react';
import EditTable from './controls/EditTable';
import ApprovalList from './controls/ApprovalList';
import { useApprovalListMode } from './controls/useApprovalListMode';
import { useActivated, useFormulaStateWithStatus, useLateVisibilityCascadeGate } from '@molecules/formbuilder/hooks';
import { EnsureExternalDataSourceLoaded, parseExternalDataSourceArrayKey } from '@utils/externalDataSourceUtils';

export interface ITableProps<TFieldValues extends object = object> {
    table: IDocumentTable;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    docId?: string;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const Table = <TFieldValues extends object = object>({
    table,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    ensureExternalDataSourceLoaded,
}: ITableProps<TFieldValues>) => {
    const { activated } = useActivated();

    // Отложенный маунт тяжёлой части смены режима: переключение isEdit перестраивает у таблицы
    // bridge, колонки (35↔41 с редакторами) и модели TanStack на все строки — на заявке с 1500
    // позициями это секунды занятого потока одним куском. Отложенный режим таблица получает
    // фоновым прерываемым рендером (transition-lane): форма переключается сразу и остаётся
    // отзывчивой, таблица показывает прежний вариант и «дозревает» следом. Граница выбрана
    // здесь, потому что это единственный узел, через который режим попадает во всю механику
    // EditTable/ApprovalList — внутри таблица всегда видит консистентную пару isEdit/viewMode.
    const deferredIsEdit = useDeferredValue(isEdit);
    const deferredIsNew = useDeferredValue(isNew);
    /** Таблица ещё в прежнем режиме, фоновый рендер дозревает — приглушение и защита от кликов. */
    const modeSettling = deferredIsEdit !== isEdit || deferredIsNew !== isNew;

    const { value: visibility, settled: visibilitySettled } = useFormulaStateWithStatus(
        table?.visibilityRules,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: true,
            ensureExternalDataSourceLoaded,
            trace: table
                ? {
                      targetId: table.id || table.key,
                      targetKind: 'table',
                      targetName: table.name || table.key,
                      ruleKind: 'visibility',
                  }
                : undefined,
        },
    );

    // Table остаётся смонтированным при скрытии таблицы (ниже рендерит <></>), поэтому именно здесь
    // (а не в перемонтируемом EditTable) отслеживаем позднее появление видимости для каскада.
    const skipCentralizedCascade = useLateVisibilityCascadeGate(table, isNew, isEdit, visibility, visibilitySettled);

    const dataField = fields[table.key];
    const approvalListMode = useApprovalListMode(table, fields, formMethods);

    useEffect(() => {
        if (dataField?.index !== undefined && !includedFields.includes(dataField.index)) {
            includedFields.push(dataField.index);
        }
    }, [dataField?.index, includedFields]);

    // Строки внешнего массива (`|ExternalDataSource|<источник>`) приходят не с
    // документом, а отдельным запросом источника — до него таблице показывать нечего.
    const externalArraySource = parseExternalDataSourceArrayKey(table.key);
    useEffect(() => {
        if (!externalArraySource || !ensureExternalDataSourceLoaded) return;
        void ensureExternalDataSourceLoaded(externalArraySource.sourceName);
    }, [externalArraySource?.sourceName, ensureExternalDataSourceLoaded, dataField?.pending]);

    if (!table?.key || !table.tableColumn || !visibility || !dataField) return <></>;

    return (
        <div
            className={modeSettling ? 'document-table-field document-table-field--mode-settling' : 'document-table-field'}
            data-testid={table.id ? `table-field-${table.id}` : undefined}
            data-document-field-index={dataField.index}
        >
            {approvalListMode && !deferredIsEdit && !deferredIsNew ? (
                // лист согласования: те же строки, но циклами маршрута (только просмотр)
                <ApprovalList table={table} formMethods={formMethods} fields={fields} docId={docId} />
            ) : (
                <EditTable
                    table={table}
                    formMethods={formMethods}
                    fields={fields}
                    docId={docId}
                    isEdit={deferredIsEdit}
                    isNew={deferredIsNew}
                    setError={setError}
                    turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                    skipCentralizedCascade={skipCentralizedCascade}
                    viewMode={!deferredIsEdit && !deferredIsNew}
                />
            )}
        </div>
    );
};

export default Table;
